import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@shared-kernel/primary/shared/shadcn/ui/collapsible";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { LongTextField } from "@shared-kernel/primary/shared/long-text-field/long-text-field";
import { LONG_TEXT_TALL_MIN_HEIGHT } from "@shared-kernel/primary/shared/long-text-field/long-text-rows";
import { Switch } from "@shared-kernel/primary/shared/shadcn/ui/switch";
import { UploadButton } from "@shared-kernel/primary/shared/upload-button/upload-button";
import { PDF_FILETYPE, PDF_MAX_FILE_SIZE } from "@shared-kernel/domain/types/file-validation";
import type { RequestState } from "@redux/state/shared-types";

interface Props {
  name: string;
  // Le hook pose l'issue du téléversement sur `${name}.${rowIndex}.documentUrl`
  onUpload: (rowIndex: number, file: File) => void;
  uploadProcessing: RequestState;
}

const METHODOLOGY_GUIDANCE_AXES = [
  {
    title: "Objectifs pédagogiques travaillés",
    description:
      "Ce que votre méthode vise à développer chez l'apprenant — respiration, justesse, interprétation, technique d'un répertoire spécifique, présence scénique, …",
  },
  {
    title: "Déroulé type d'une séance",
    description: "Le déroulé concret d'une séance avec vous — échauffement, exercices, mise en pratique, bilan, …",
  },
  {
    title: "Outils et supports utilisés",
    description: "Supports écrits, images, objets, partitions, enregistrements, piano/instrument, applications, captations vidéo, …",
  },
  {
    title: "Adaptation aux apprenants",
    description:
      "Votre façon d'ajuster votre enseignement selon le niveau, les objectifs et les besoins de chaque apprenant et donc de le personnaliser.",
  },
  {
    title: "Suivi des progrès",
    description:
      "Votre façon de suivre et d'évaluer l'évolution de chaque apprenant au fil des séances — les repères que vous utilisez pour mesurer les progrès accomplis vis-à-vis des résultats attendus.",
  },
];

interface RowProps {
  name: string;
  rowIndex: number;
  onRemove: () => void;
  onUpload: (rowIndex: number, file: File) => void;
  isUploading: boolean;
}

const TeachingMethodologyRow = ({ name, rowIndex, onRemove, onUpload, isUploading }: RowProps) => {
  const { control, setValue, setError, clearErrors } = useFormContext();
  const documentField = `${name}.${rowIndex}.documentUrl`;
  const descriptionFieldName = `${name}.${rowIndex}.description`;

  const handleFileSelect = (file: File) => {
    clearErrors(documentField);
    onUpload(rowIndex, file);
  };

  const handleFileError = () => {
    setError(documentField, { type: "manual", message: "Veuillez télécharger un fichier PDF de 10 Mo max" });
  };

  const handleFileDelete = () => {
    clearErrors(documentField);
    setValue(documentField, null, { shouldDirty: true });
  };

  return (
    <div className="rounded-md border bg-card p-3" data-testid={`teaching-methodology-row-${rowIndex}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm font-medium">Méthodologie {rowIndex + 1}</p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
          data-testid={`teaching-methodology-remove-${rowIndex}`}
        >
          <Trash2 className="size-4" />
          <span className="sr-only">Supprimer cette méthodologie</span>
        </Button>
      </div>
      <div className="space-y-3">
        <FormField
          name={`${name}.${rowIndex}.title`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Titre</FormLabel>
              <FormControl>
                <Input {...field} value={field.value ?? ""} data-testid={`teaching-methodology-title-${rowIndex}`} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <LongTextField
          control={control}
          name={descriptionFieldName}
          label="Description"
          className={LONG_TEXT_TALL_MIN_HEIGHT}
          data-testid={`teaching-methodology-description-${rowIndex}`}
        />
        <FormField
          name={documentField}
          render={({ field, fieldState: { error } }) => (
            <FormItem>
              <UploadButton
                processing={isUploading ? "pending" : "idle"}
                label="Document (optionnel)"
                buttonText="Télécharger un document (pdf)"
                onFileSelect={handleFileSelect}
                onError={handleFileError}
                accept={PDF_FILETYPE}
                maxSize={PDF_MAX_FILE_SIZE}
                value={field.value ?? null}
                onFileDelete={handleFileDelete}
                helpText="Télécharger un document PDF de 10 Mo max"
                error={error?.message}
                data-testid={`teaching-methodology-document-${rowIndex}`}
              />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${name}.${rowIndex}.isPublic`}
          render={({ field }) => (
            <FormItem className="flex flex-row items-start justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-1">
                <FormLabel>Partager sur le profil public</FormLabel>
                <p className="text-sm text-muted-foreground">
                  {field.value
                    ? "Cette méthodologie est visible sur votre profil public."
                    : "Cette méthodologie n’apparaît pas sur votre profil public."}
                </p>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={checked => setValue(`${name}.${rowIndex}.isPublic`, checked, { shouldDirty: true })}
                  data-testid={`teaching-methodology-is-public-${rowIndex}`}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export const TeachingMethodologiesField = ({ name, onUpload, uploadProcessing }: Props) => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name, keyName: "fieldId" });
  // Dernière ligne dont un document a été choisi : elle porte le spinner tant que le téléversement est en cours
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  const handleUpload = (rowIndex: number, file: File) => {
    setSelectedRowIndex(rowIndex);
    onUpload(rowIndex, file);
  };

  return (
    <FormField
      name={name}
      render={() => (
        <FormItem>
          <FormLabel>Méthodologies pédagogiques</FormLabel>
          <FormDescription>
            Description détaillée de votre approche pédagogique (au moins une). Ce contenu documente concrètement le déroulé d'une séance ou
            d'une série de séances et constitue une exigence de tous les financeurs et contrôleurs (AFDAS, France Travail, Qualiopi, …).
          </FormDescription>
          <Collapsible defaultOpen className="text-sm text-muted-foreground">
            <CollapsibleTrigger className="group flex items-center gap-1 text-left underline underline-offset-2">
              Afficher les conseils détaillés (5 axes recommandés)
              <ChevronDown className="size-3 transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-2">
              <ul className="list-disc space-y-1 pl-5">
                {METHODOLOGY_GUIDANCE_AXES.map(axis => (
                  <li key={axis.title}>
                    <span className="font-bold">{axis.title}</span> — {axis.description}
                  </li>
                ))}
              </ul>
              <p>
                Visez <span className="font-bold">15 à 20 lignes minimum</span> par méthodologie. Vous pouvez ajouter un ou plusieurs{" "}
                <span className="font-bold">PDF</span> pour densifier votre description.
              </p>
            </CollapsibleContent>
          </Collapsible>
          <div className="space-y-3">
            {fields.map((field, rowIndex) => (
              <TeachingMethodologyRow
                key={(field as { fieldId: string }).fieldId}
                name={name}
                rowIndex={rowIndex}
                onRemove={() => remove(rowIndex)}
                onUpload={handleUpload}
                isUploading={uploadProcessing === "pending" && selectedRowIndex === rowIndex}
              />
            ))}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => append({ title: "", description: "", documentUrl: null, isPublic: false })}
              data-testid="teaching-methodology-add"
            >
              <Plus className="size-4" />
              Ajouter une méthodologie
            </Button>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
